/* ============================================================================
   BARKLIN — cart-bar.js (Hardened / Null-safe)
   Goal:
   - NEVER crash if <cart-bar> is rendered outside <product-info>
   - NEVER crash if selectors are missing (theme/template drift)
   - Keep original behavior when context exists
   Notes:
   - Drop-in replacement for assets/cart-bar.js
   - Works with Broadcast-style "theme:*" events
   ============================================================================ */
!function(){"use strict";

  class CartBar extends HTMLElement {
    constructor(){
      super();

      // Guarded context discovery
      this.section = this.closest("product-info") || null;

      // These will be resolved safely
      this.form = null;
      this.formWrapper = null;

      // Bindings
      this.handleProductAddError = this.handleProductAddError.bind(this);
      this.boundToggleCartBarOnScroll = this.toggleCartBarOnScroll.bind(this);
      this.boundSetCartBarHeight = this.setCartBarHeight.bind(this);

      // State
      this._disabled = false;
    }

    connectedCallback(){
      // Resolve dependencies at connect-time (DOM can change)
      this._resolveContext();

      // If no valid context, disable quietly (no crash)
      if(this._disabled) return;

      this.setCartBarHeight();

      this.addEventListener("click", this.handleClick.bind(this));

      document.addEventListener("theme:scroll", this.boundToggleCartBarOnScroll);
      document.addEventListener("theme:resize", this.boundSetCartBarHeight);
      document.addEventListener("theme:product:add-error", this.handleProductAddError);

      // Initial visibility check (optional safety)
      this.toggleCartBarOnScroll();
    }

    disconnectedCallback(){
      // Always safe to remove listeners
      document.removeEventListener("theme:product:add-error", this.handleProductAddError);
      document.removeEventListener("theme:scroll", this.boundToggleCartBarOnScroll);
      document.removeEventListener("theme:resize", this.boundSetCartBarHeight);
    }

    _resolveContext(){
      // If section was null in constructor, try again (in case of late rendering)
      if(!this.section) this.section = this.closest("product-info") || null;

      // If still no section, disable
      if(!this.section){
        this._disabled = true;
        return;
      }

      // Resolve form (required for add-to-cart dispatch)
      this.form = this.section.querySelector("[data-product-form]") || null;

      if(!this.form){
        this._disabled = true;
        return;
      }

      // Wrapper used by sticky logic
      const stickyEnabled = !!(window.theme && window.theme.settings && window.theme.settings.productPageSticky);
      this.formWrapper = (stickyEnabled && this.section.querySelector("[data-form-wrapper]")) || this.form;

      this._disabled = false;
    }

    handleClick(evt){
      if(this._disabled) return;

      // Re-resolve if DOM changed
      if(!this.form || !this.section) this._resolveContext();
      if(this._disabled) return;

      const addBtnProxy = evt.target && evt.target.closest ? evt.target.closest("[data-cart-bar-add-to-cart]") : null;
      if(addBtnProxy){
        evt.preventDefault();

        addBtnProxy.classList.add("is-loading");
        addBtnProxy.setAttribute("disabled","disabled");

        const realAddBtn = this.form.querySelector("[data-add-to-cart]");
        if(realAddBtn){
          realAddBtn.dispatchEvent(new Event("click", { bubbles: true }));
        } else {
          // No real button found → stop loading state
          addBtnProxy.classList.remove("is-loading");
          addBtnProxy.removeAttribute("disabled");
        }
        return;
      }

      const scrollBtn = evt.target && evt.target.closest ? evt.target.closest("[data-cart-bar-scroll]") : null;
      if(scrollBtn){
        evt.preventDefault();
        this.scrollToTop();
        return;
      }
    }

    handleProductAddError(){
      if(this._disabled) return;
      // If cart bar has button, scroll up so error is visible
      if(this.querySelector && this.querySelector("[data-cart-bar-add-to-cart]")){
        this.scrollToTop();
      }
    }

    scrollToTop(){
      if(this._disabled) return;

      if(!this.section || !this.form) this._resolveContext();
      if(this._disabled) return;

      const variantSelects = this.section.querySelector("variant-selects");
      const isMobile = !!(window.theme && typeof window.theme.isMobile === "function" && window.theme.isMobile());

      // Choose anchor
      const anchor = isMobile ? (variantSelects || this.form) : this.section;
      if(!anchor || !anchor.getBoundingClientRect) return;

      const top = anchor.getBoundingClientRect().top;

      // Broadcast uses window.theme.scrollTo
      if(window.theme && typeof window.theme.scrollTo === "function"){
        window.theme.scrollTo(isMobile ? top - 10 : top);
      } else {
        // Fallback
        window.scrollTo({ top: window.scrollY + top - (isMobile ? 10 : 0), behavior: "smooth" });
      }
    }

    toggleCartBarOnScroll(){
      if(this._disabled) return;

      if(!this.formWrapper) this._resolveContext();
      if(this._disabled) return;

      const y = window.scrollY || 0;
      if(this.formWrapper && typeof this.formWrapper.offsetTop === "number"){
        const threshold = this.formWrapper.offsetTop + this.formWrapper.offsetHeight;
        const shouldShow = y > threshold;
        this.classList.toggle("is-visible", shouldShow);
      }
    }

    setCartBarHeight(){
      // Even if disabled, setting height is safe
      const h = this.offsetHeight || 0;
      document.documentElement.style.setProperty("--cart-bar-height", `${h}px`);
    }
  }

  if(!customElements.get("cart-bar")){
    customElements.define("cart-bar", CartBar);
  }

}();
